/**
 * World map — 19 hexes, deposit composition, depletion. spec §12.
 *
 * The pixel layout uses the SAME axial basis as the simulation's distance
 * maths (src/sim/hex.ts): x = spacing * (q + r/2), y = spacing * r * sqrt(3)/2.
 * That is what makes the picture agree with the arithmetic — the six ring-2
 * hexes that are 15,000 m away genuinely look nearer than the six at 17,320 m,
 * rather than the map implying a uniform ring while drone timings disagree.
 *
 * Neighbours therefore sit at 0, +/-60, +/-120 and 180 degrees, which means each
 * hex has flat edges east and west and a vertex at the top: a pointy-top
 * hexagon, with vertices at 30 + 60k degrees.
 *
 * Reads a state snapshot and draws. It never touches the simulation.
 */

import {
  DECITICKS_PER_TICK,
  HEX_CENTER_SPACING_M,
  HEX_SIDE_M,
} from '../../sim/constants';
import { MINERAL_IDS, type DroneClass, type MineralId } from '../../sim/data';
import { travelDeciTicks } from '../../sim/drones';
import { hexDistanceMetres, hexKey } from '../../sim/hex';
import type { Drone, GameState, HexCoord } from '../../sim/types';
import { COLORS, MINERAL_COLORS, prepareCanvas } from '../theme';

const SQRT3_OVER_2 = Math.sqrt(3) / 2;

/** Extent of the 2-ring world in metres, including the outer hexes' bodies. */
const WORLD_HALF_WIDTH_M = 2 * HEX_CENTER_SPACING_M + HEX_SIDE_M;
const WORLD_HALF_HEIGHT_M = 2 * HEX_CENTER_SPACING_M * SQRT3_OVER_2 + HEX_SIDE_M;

export interface WorldMapLayout {
  /** Pixels per metre. */
  scale: number;
  centerX: number;
  centerY: number;
}

export function worldMapLayout(cssWidth: number, cssHeight: number): WorldMapLayout {
  const padding = 8;
  const usableWidth = Math.max(1, cssWidth - padding * 2);
  const usableHeight = Math.max(1, cssHeight - padding * 2);

  const scale = Math.min(
    usableWidth / (WORLD_HALF_WIDTH_M * 2),
    usableHeight / (WORLD_HALF_HEIGHT_M * 2),
  );

  return { scale, centerX: cssWidth / 2, centerY: cssHeight / 2 };
}

/** Pixel centre of a hex. Mirrors src/sim/hex.ts hexDistanceMetres exactly. */
export function hexCenterPixel(
  layout: WorldMapLayout,
  coord: HexCoord,
): { x: number; y: number } {
  const metresX = HEX_CENTER_SPACING_M * (coord.q + coord.r / 2);
  const metresY = HEX_CENTER_SPACING_M * coord.r * SQRT3_OVER_2;
  return {
    x: layout.centerX + metresX * layout.scale,
    y: layout.centerY + metresY * layout.scale,
  };
}

/** Which hex, if any, is under a pixel. Nearest-centre within one hex radius. */
export function hexAtPixel(
  layout: WorldMapLayout,
  state: GameState,
  px: number,
  py: number,
): HexCoord | null {
  const radius = HEX_SIDE_M * layout.scale;
  let best: HexCoord | null = null;
  let bestDistance = radius;

  for (const key of Object.keys(state.planet.hexes).sort()) {
    const hex = state.planet.hexes[key];
    if (hex === undefined) continue;
    const center = hexCenterPixel(layout, hex.coord);
    const distance = Math.hypot(px - center.x, py - center.y);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = hex.coord;
    }
  }

  return best;
}

function tracePointyTopHex(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
): void {
  // Vertices at 30 + 60k degrees puts a point at the top and flat edges on the
  // east and west sides, matching the neighbour directions above.
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (30 + 60 * i);
    points.push({ x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) });
  }

  // Rounded corners, so a hex reads as the same kind of object as the controls
  // around it rather than as a hard geometric cell. Each corner is an arc
  // between the midpoints of the two edges that meet there, which is why the
  // path starts at a midpoint rather than at a vertex.
  const corner = radius * 0.24;
  const mid = (a: { x: number; y: number }, b: { x: number; y: number }) => ({
    x: (a.x + b.x) / 2,
    y: (a.y + b.y) / 2,
  });

  const first = mid(points[0]!, points[1]!);
  ctx.beginPath();
  ctx.moveTo(first.x, first.y);
  for (let i = 1; i <= 6; i++) {
    const vertex = points[i % 6]!;
    const next = points[(i + 1) % 6]!;
    const target = mid(vertex, next);
    ctx.arcTo(vertex.x, vertex.y, target.x, target.y, corner);
  }
  ctx.closePath();
}

function totalOf(deposits: Record<MineralId, number>): number {
  let total = 0;
  for (const id of MINERAL_IDS) total += deposits[id];
  return total;
}

/**
 * Composition bar: one stacked segment per mineral still present, sized by its
 * share of what remains in that hex.
 *
 * Shows share rather than absolute quantity, because absolute bars across a
 * world where one hex holds 40x another would render every poor hex as an
 * invisible sliver. Absolute richness is carried by the hex's fill opacity
 * instead, so the two readings do not compete for the same visual channel.
 */
/** How many minerals contribute to a hex's colour. */
const BLEND_DEPTH = 3;

/** #rrggbb to its three channels. */
function channels(colour: string): [number, number, number] {
  const value = Number.parseInt(colour.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

/** sRGB channel to linear light, and back. */
function toLinear(channel: number): number {
  const v = channel / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

function toSrgb(linear: number): number {
  const v = linear <= 0.0031308 ? linear * 12.92 : 1.055 * linear ** (1 / 2.4) - 0.055;
  return Math.max(0, Math.min(255, Math.round(v * 255)));
}

/**
 * The colour of a hex: its three largest minerals blended in proportion.
 *
 * This replaces the stacked bar that used to sit in the middle of every hex.
 * A bar is read one hex at a time; a colour is read across the whole map at
 * once, which is the actual question the player has — where do I send a drone
 * for iron. Three rather than all eight because blending eight earth tones
 * returns brown for every hex on the map, and the tail of a strip-mined mix is
 * noise the player cannot act on anyway.
 *
 * Blended in LINEAR LIGHT, not in sRGB. Averaging gamma-encoded channels
 * darkens and desaturates every mixture — the classic muddy midpoint — and
 * these are already earth tones with little room to spare.
 */
function hexColour(deposits: Record<MineralId, number>): string | null {
  const ranked = MINERAL_IDS
    .map((id) => ({ id, amount: deposits[id] }))
    .filter((entry) => entry.amount > 0)
    // Amount first, then the spec's own mineral order, so a tie between two
    // equal deposits resolves the same way every frame.
    .sort((a, b) =>
      b.amount - a.amount || MINERAL_IDS.indexOf(a.id) - MINERAL_IDS.indexOf(b.id),
    )
    .slice(0, BLEND_DEPTH);

  if (ranked.length === 0) return null;

  const total = ranked.reduce((sum, entry) => sum + entry.amount, 0);
  let r = 0;
  let g = 0;
  let b = 0;
  for (const entry of ranked) {
    const weight = entry.amount / total;
    const [cr, cg, cb] = channels(MINERAL_COLORS[entry.id]);
    r += toLinear(cr) * weight;
    g += toLinear(cg) * weight;
    b += toLinear(cb) * weight;
  }

  return `rgb(${toSrgb(r)}, ${toSrgb(g)}, ${toSrgb(b)})`;
}

/**
 * The factory ship on its landing site.
 *
 * It is an OCTAHEDRON — an eight-sided diamond — that drove its lower half into
 * the ground on a deliberate landing, so what stands above the surface is a
 * four-sided pyramid. The buried half is not drawn. It was, in dashes, and that read as an
 * x-ray diagram rather than as a ship: what sells "half of this is underground"
 * is not showing the hidden part but showing the GROUND being displaced by it.
 *
 * So, in order: a cast shadow, the soil it displaced, the two lit faces, then
 * a contact arc drawn back OVER the hull's foot. That last one is what does the
 * work — the near edge of the ground passing in front of the hull is the whole
 * difference between an object standing on a surface and one sunk into it.
 *
 * ISOMETRIC, not perspective: a square base seen from above and to one side is
 * a rhombus twice as wide as it is deep, which is the 2:1 ratio below. The four
 * base corners are left/right on the horizontal and front/back on the depth
 * axis; from this viewpoint the two front faces are visible and the two back
 * faces are behind the hull. An ellipse through those same four corners is the
 * ground line, which is why the contact arc and the displaced soil share radii.
 *
 * Everything is derived from `radius`, so it stays right at any zoom.
 */
function drawFactoryShip(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  radius: number,
): void {
  const halfWidth = radius * 0.5;
  const halfDepth = halfWidth * 0.5;
  const height = radius * 0.62;

  const apex = { x: cx, y: cy - height };
  const left = { x: cx - halfWidth, y: cy };
  const right = { x: cx + halfWidth, y: cy };
  const front = { x: cx, y: cy + halfDepth };

  ctx.save();
  ctx.lineJoin = 'round';

  // --- cast shadow ----------------------------------------------------------
  // Offset down and right, because the left face is the lit one.
  ctx.globalAlpha = 0.45;
  ctx.fillStyle = COLORS.background;
  ctx.beginPath();
  ctx.ellipse(
    cx + halfWidth * 0.22,
    cy + halfDepth * 0.42,
    halfWidth * 1.5,
    halfDepth * 1.5,
    0,
    0,
    Math.PI * 2,
  );
  ctx.fill();

  // --- displaced soil -------------------------------------------------------
  // Pushed aside as the hull seated itself. An ellipse rather than a rhombus:
  // ground does not have corners.
  ctx.globalAlpha = 0.3;
  ctx.strokeStyle = COLORS.factory;
  ctx.lineWidth = Math.max(1, radius * 0.022);
  ctx.beginPath();
  ctx.ellipse(cx, cy, halfWidth * 1.45, halfDepth * 1.45, 0, 0, Math.PI * 2);
  ctx.stroke();

  // --- the two visible faces ------------------------------------------------
  // Different values on the two faces is the whole of the form: flat-filled,
  // the pyramid would read as a triangle.
  ctx.globalAlpha = 0.9;
  ctx.fillStyle = COLORS.factory;
  ctx.beginPath();
  ctx.moveTo(apex.x, apex.y);
  ctx.lineTo(left.x, left.y);
  ctx.lineTo(front.x, front.y);
  ctx.closePath();
  ctx.fill();

  ctx.globalAlpha = 0.55;
  ctx.beginPath();
  ctx.moveTo(apex.x, apex.y);
  ctx.lineTo(front.x, front.y);
  ctx.lineTo(right.x, right.y);
  ctx.closePath();
  ctx.fill();

  // --- lit edges ------------------------------------------------------------
  ctx.globalAlpha = 1;
  ctx.shadowColor = COLORS.factory;
  ctx.shadowBlur = Math.max(6, radius * 0.2);
  ctx.strokeStyle = COLORS.factory;
  ctx.lineWidth = Math.max(1, radius * 0.045);
  ctx.beginPath();
  ctx.moveTo(left.x, left.y);
  ctx.lineTo(apex.x, apex.y);
  ctx.lineTo(right.x, right.y);
  ctx.stroke();

  // The hip down the middle, which separates the two faces and stops the
  // silhouette reading as a flat kite.
  ctx.beginPath();
  ctx.moveTo(apex.x, apex.y);
  ctx.lineTo(front.x, front.y);
  ctx.stroke();

  // --- the contact arc ------------------------------------------------------
  // The near half of the ground line, drawn last so it passes IN FRONT of the
  // hull's foot. This is what makes the ship sit in the soil rather than on it.
  ctx.shadowBlur = 0;
  ctx.globalAlpha = 0.85;
  ctx.lineWidth = Math.max(1, radius * 0.035);
  ctx.beginPath();
  ctx.ellipse(cx, cy, halfWidth, halfDepth, 0, 0, Math.PI);
  ctx.stroke();

  ctx.restore();
}

export function drawWorldMap(
  canvas: HTMLCanvasElement,
  state: GameState,
  cssWidth: number,
  cssHeight: number,
  devicePixelRatio: number,
  selected?: HexCoord | null,
): void {
  const ctx = prepareCanvas(canvas, cssWidth, cssHeight, devicePixelRatio);
  if (ctx === null) return;

  ctx.fillStyle = COLORS.background;
  ctx.fillRect(0, 0, cssWidth, cssHeight);

  const layout = worldMapLayout(cssWidth, cssHeight);
  const radius = HEX_SIDE_M * layout.scale;
  if (radius < 2) return;

  const keys = Object.keys(state.planet.hexes).sort();

  for (const key of keys) {
    const hex = state.planet.hexes[key];
    if (hex === undefined) continue;

    const { x: cx, y: cy } = hexCenterPixel(layout, hex.coord);
    const initial = totalOf(hex.initialDeposits);
    const remaining = totalOf(hex.deposits);
    // Depletion as fill opacity, per spec §12: a hex visibly fades as it is
    // stripped. Floor at 0.08 so an exhausted hex is still locatable.
    const fraction = initial <= 0 ? 0 : remaining / initial;

    // What is in the ground decides the colour; how much is left decides how
    // strongly it shows.
    const colour = hexColour(hex.deposits) ?? COLORS.panel;

    tracePointyTopHex(ctx, cx, cy, radius);

    // A floor of the panel colour, so an exhausted hex is still a locatable
    // object rather than a hole in the map.
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = COLORS.panel;
    ctx.fill();
    ctx.globalAlpha = 1;

    if (fraction > 0) {
      ctx.globalAlpha = 0.16 + 0.54 * fraction;
      ctx.fillStyle = colour;
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // A lit edge in the same colour, dimming as the hex is stripped, so both
    // what a hex holds and how much is left read from across the map.
    ctx.save();
    ctx.shadowColor = colour;
    ctx.shadowBlur = 12;
    ctx.globalAlpha = 0.35 + 0.45 * fraction;
    ctx.strokeStyle = colour;
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.restore();
  }

  // The landing site, drawn after the bodies so it is never overdrawn.
  const home = state.planet.hexes[hexKey({ q: 0, r: 0 })];
  if (home !== undefined && radius >= 12) {
    const { x, y } = hexCenterPixel(layout, home.coord);
    drawFactoryShip(ctx, x, y - radius * 0.06, radius);

    ctx.fillStyle = COLORS.factory;
    ctx.font = `bold ${Math.max(8, Math.round(radius * 0.19))}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('HOME', x, y + radius * 0.62);
  }

  if (selected != null) {
    const { x, y } = hexCenterPixel(layout, selected);
    tracePointyTopHex(ctx, x, y, radius);
    ctx.save();
    ctx.shadowColor = COLORS.accent;
    ctx.shadowBlur = 22;
    ctx.strokeStyle = COLORS.accent;
    ctx.lineWidth = 3;
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.restore();
  }
}

/**
 * Drone overlay, drawn on its own transparent canvas above the world map.
 *
 * Two canvases rather than one because spec §12 wants the map redrawn on state
 * change only, while drone motion is interpolated every animation frame. Sharing
 * a canvas would force a full map repaint at 60 Hz to animate a handful of dots.
 *
 * `alpha` is the fraction of the current tick already elapsed, in 0..1. It is
 * purely cosmetic — the simulation knows only phase and deci-ticks remaining,
 * and nothing here feeds back into it.
 */
/**
 * The airframe each class flies, so the fleet is readable at a glance rather
 * than only through the roster. A heavy is the one that changes the shape of a
 * run — 200 units against a mini's 50 — and it should not take a click to find
 * out how many are in the air.
 *
 * `size` scales the whole aircraft; `rotors` is how many are spaced evenly
 * around it.
 */
const AIRFRAMES: Record<DroneClass, { rotors: number; size: number }> = {
  standard: { rotors: 6, size: 1 },
  heavy: { rotors: 12, size: 1.45 },
};

/**
 * One drone: a hexacopter seen from directly above — a body with six rotors
 * on arms around it.
 *
 * NO HEADING. A six-rotor aircraft is radially symmetric and these are drawn
 * from above, so there is nothing to rotate; a drone flying home looks the same
 * as one flying out, which is correct rather than a shortcut.
 *
 * The rotors are drawn before the body so the arms disappear underneath it
 * rather than crossing the middle, and the whole thing is outlined in the
 * background colour so it stays legible over any hex hue beneath.
 */
function drawDrone(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  scale: number,
  colour: string,
  airframe: { rotors: number; size: number },
): void {
  const { rotors: rotorCount, size } = airframe;
  const body = Math.max(1.6, scale * 0.045) * size;
  // Twelve rotors on a ring only a little larger than six would overlap into a
  // solid annulus, so each rotor shrinks as the count goes up while the ring
  // grows: the heavy reads as a bigger, busier aircraft rather than a blob.
  const rotor = Math.max(0.9, scale * 0.03) * size * (6 / rotorCount) ** 0.5;
  const arm = Math.max(3.2, scale * 0.088) * size;

  ctx.save();

  // Arms first, under everything.
  ctx.globalAlpha = 0.75;
  ctx.strokeStyle = colour;
  ctx.lineWidth = Math.max(0.75, scale * 0.012);
  ctx.beginPath();
  for (let i = 0; i < rotorCount; i++) {
    const angle = (Math.PI * 2 * i) / rotorCount;
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(angle) * arm, y + Math.sin(angle) * arm);
  }
  ctx.stroke();

  // Rotors.
  ctx.globalAlpha = 1;
  ctx.fillStyle = colour;
  ctx.strokeStyle = COLORS.background;
  ctx.lineWidth = Math.max(0.6, scale * 0.008);
  for (let i = 0; i < rotorCount; i++) {
    const angle = (Math.PI * 2 * i) / rotorCount;
    ctx.beginPath();
    ctx.arc(x + Math.cos(angle) * arm, y + Math.sin(angle) * arm, rotor, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }

  // Body last, so the arms end underneath it.
  ctx.beginPath();
  ctx.arc(x, y, body, 0, Math.PI * 2);
  ctx.fill();
  ctx.lineWidth = Math.max(0.8, scale * 0.01);
  ctx.stroke();

  ctx.restore();
}

export function drawDroneLayer(
  canvas: HTMLCanvasElement,
  state: GameState,
  alpha: number,
  cssWidth: number,
  cssHeight: number,
  devicePixelRatio: number,
): void {
  const ctx = prepareCanvas(canvas, cssWidth, cssHeight, devicePixelRatio);
  if (ctx === null) return;

  const layout = worldMapLayout(cssWidth, cssHeight);
  const radius = HEX_SIDE_M * layout.scale;
  if (radius < 2) return;

  const home = hexCenterPixel(layout, { q: 0, r: 0 });

  // Zoomed far enough out, six rotors and a body land on the same three pixels
  // and turn to mush; a plain dot says "a drone is here" better than a smudge
  // of one does.
  const detailed = radius >= 20;
  const dotRadius = Math.max(2, radius * 0.07);

  for (const drone of [...state.drones].sort((a, b) => a.id - b.id)) {
    const position = dronePixel(layout, state, drone, alpha, home);
    if (position === null) continue;

    // Laden drones take the factory colour, empty ones stay dim: the direction
    // of traffic is readable without following any single drone.
    const colour = drone.cargo === null ? COLORS.textDim : COLORS.factory;

    if (detailed) {
      drawDrone(ctx, position.x, position.y, radius, colour, AIRFRAMES[drone.class]);
      continue;
    }

    ctx.beginPath();
    ctx.arc(position.x, position.y, dotRadius, 0, Math.PI * 2);
    ctx.fillStyle = colour;
    ctx.fill();
    ctx.strokeStyle = COLORS.background;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}

function dronePixel(
  layout: WorldMapLayout,
  state: GameState,
  drone: Drone,
  alpha: number,
  home: { x: number; y: number },
): { x: number; y: number } | null {
  if (drone.phase === 'idle' || drone.assignedHex === null) return null;

  const hex = state.planet.hexes[hexKey(drone.assignedHex)];
  if (hex === undefined) return null;

  const target = hexCenterPixel(layout, hex.coord);
  const travel = travelDeciTicks(
    hexDistanceMetres({ q: 0, r: 0 }, hex.coord),
    drone.speedKmPerTick,
  );
  if (travel <= 0) return target;

  // Interpolating inside the tick: the stored counter only moves once per tick,
  // so without this the dots would jump rather than travel.
  const remaining = Math.max(0, drone.phaseDeciTicksRemaining - alpha * DECITICKS_PER_TICK);
  const progress = Math.min(1, Math.max(0, 1 - remaining / travel));

  switch (drone.phase) {
    case 'outbound':
      return lerp(home, target, progress);
    case 'inbound':
      return lerp(target, home, progress);
    case 'mining':
      return target;
    case 'unloading':
      return home;
    default:
      return null;
  }
}

function lerp(
  from: { x: number; y: number },
  to: { x: number; y: number },
  t: number,
): { x: number; y: number } {
  return { x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t };
}
